import React from "react";
import {
  Activity, Bike, Footprints, Waves, Mountain, Flame, Zap, Target,
  Dumbbell, Heart, Apple, Droplet, Leaf, Medal,
  Moon, Wind, Music, Sparkles,
  Banana, Salad, Milk,
  Anvil, Bed, Hammer, Wrench, Cog, Box,
  Snowflake, Rocket, Compass, MapPin, MoveUpRight,
  Sun, Cloud, Star, Feather,
  Cherry, Carrot, Egg, Beef, Soup, Coffee,
  Trophy, Award, Crown, Gift, Diamond,
  Circle, CircleDot, Hexagon, Square, Triangle, Octagon, Disc, Layers, Pentagon,
  Hourglass, Grip, Layers3,
} from "lucide-react";

// Chinese numerals 1-9 mapped onto each tile so the face reads like a real
// mahjong piece. Honor tiles use 東/南/西 / 北/中/發/白 to mirror the user's
// reference image.
const NUMERAL_BY_RANK = {
  "1": "一", "2": "二", "3": "三", "4": "四", "5": "五",
  "6": "六", "7": "七", "8": "八", "9": "九",
  "α": "東", "β": "南", "γ": "西", "δ": "北",
  "中": "中", "發": "發", "白": "白",
};

// Suit colours mirror the reference image: cardio = red, strength = blue,
// equipment/mind = green, nutrition = amber, reward = warm red/gold.
const SUIT_COLOR = {
  cardio:    "#B62A2A",
  strength:  "#1E4F8A",
  equipment: "#2F5E3A",
  mind:      "#2F5E3A",
  nutrition: "#9A6F22",
  reward:    "#B62A2A",
};

// Each tile keeps a Lucide fallback icon (`I`) for environments where the
// generated PNG hasn't loaded yet, but the primary visual is the AI-generated
// fitness illustration at /tiles/{tile_type}.png.
export const TILE_META = {
  // CARDIO (red 萬-suit) — athlete poses
  fit_run:      { I: Footprints,  s: "cardio",   r: "1" },
  fit_bike:     { I: Bike,        s: "cardio",   r: "2" },
  fit_row:      { I: Waves,       s: "cardio",   r: "3" },
  fit_jumprope: { I: MoveUpRight, s: "cardio",   r: "4" },
  fit_swim:     { I: Droplet,     s: "cardio",   r: "5" },
  fit_hike:     { I: Mountain,    s: "cardio",   r: "6" },
  fit_walk:     { I: Compass,     s: "cardio",   r: "7" },
  fit_dance:    { I: Music,       s: "cardio",   r: "8" },
  fit_box:      { I: Zap,         s: "cardio",   r: "9" },
  fit_skate:    { I: Snowflake,   s: "cardio",   r: "α" },
  fit_sprint:   { I: Rocket,      s: "cardio",   r: "β" },
  fit_stairs:   { I: MapPin,      s: "cardio",   r: "γ" },

  // STRENGTH (blue weight-plate suit) — distinct strength icons
  str_dumbbell:  { I: Dumbbell,   s: "strength", r: "1" },
  str_barbell:   { I: Box,        s: "strength", r: "2" },
  str_kettlebell:{ I: Anvil,      s: "strength", r: "3" },
  str_squat:     { I: Activity,   s: "strength", r: "4" },
  str_bench:     { I: Bed,        s: "strength", r: "5" },
  str_deadlift:  { I: Hammer,     s: "strength", r: "6" },
  str_curl:      { I: Wrench,     s: "strength", r: "7" },
  str_pushup:    { I: Cog,        s: "strength", r: "8" },
  str_pullup:    { I: Award,      s: "strength", r: "9" },
  str_plank:     { I: Feather,    s: "strength", r: "α" },

  // EQUIPMENT/MIND (green suit)
  mind_yoga:     { I: Leaf,       s: "mind",     r: "1" },
  mind_meditate: { I: Moon,       s: "mind",     r: "2" },
  mind_stretch:  { I: Wind,       s: "mind",     r: "3" },
  mind_balance:  { I: Sun,        s: "mind",     r: "4" },

  nut_apple:   { I: Apple,    s: "nutrition", r: "1" },
  nut_water:   { I: Cloud,    s: "nutrition", r: "2" },
  nut_shake:   { I: Milk,     s: "nutrition", r: "3" },
  nut_greens:  { I: Salad,    s: "nutrition", r: "4" },
  nut_banana:  { I: Banana,   s: "nutrition", r: "5" },

  rew_flame:  { I: Flame,    s: "reward", r: "1" },
  rew_medal:  { I: Medal,    s: "reward", r: "2" },
  rew_heart:  { I: Heart,    s: "reward", r: "3" },
  rew_bolt:   { I: Trophy,   s: "reward", r: "4" },
  rew_target: { I: Target,   s: "reward", r: "5" },

  // BONUS — these mirror the bottom row of the reference image: 中 (bodybuilder),
  // 發 (laurel kettlebell), 白 (shaker), and seasonal winds (sun/palm).
  bonus_star:    { I: Star,    s: "reward", r: "中" },
  bonus_crown:   { I: Crown,   s: "mind",   r: "發" },
  bonus_gift:    { I: Gift,    s: "strength", r: "α" },
  bonus_diamond: { I: Diamond, s: "strength", r: "β" },

  food_cherry:   { I: Cherry,  s: "nutrition", r: "6" },
  food_carrot:   { I: Carrot,  s: "nutrition", r: "7" },
  food_egg:      { I: Egg,     s: "nutrition", r: "8" },
  food_beef:     { I: Beef,    s: "nutrition", r: "9" },
  food_soup:     { I: Soup,    s: "nutrition", r: "α" },
  food_coffee:   { I: Coffee,  s: "nutrition", r: "β" },
  sparkle_zen:   { I: Sparkles, s: "mind", r: "5" },

  // PLATE-DOT STRENGTH (blue suit) — each rank gets a UNIQUE Lucide fallback
  // so two different `plate_*` types don't look identical when their PNG
  // fails to load (testers reported pairs that "looked the same" but
  // wouldn't match — root cause was every plate_* sharing the Box icon).
  plate_1: { I: Circle,        s: "strength", r: "1" },
  plate_2: { I: CircleDot,     s: "strength", r: "2" },
  plate_3: { I: Hexagon,       s: "strength", r: "3" },
  plate_4: { I: Square,        s: "strength", r: "4" },
  plate_5: { I: Triangle,      s: "strength", r: "5" },
  plate_6: { I: Octagon,       s: "strength", r: "6" },
  plate_7: { I: Disc,          s: "strength", r: "7" },
  plate_8: { I: Layers,        s: "strength", r: "8" },
  plate_9: { I: Pentagon,      s: "strength", r: "9" },

  // EQUIPMENT DOT TILES (green suit) — UNIQUE Lucide icons per type.
  // Previously eq_grippers + str_curl both used Wrench → tester confusion.
  eq_foamroll:           { I: Hourglass,    s: "equipment", r: "1" },
  eq_dumbbells:          { I: Dumbbell,     s: "equipment", r: "2" },
  eq_resistband:         { I: Activity,     s: "equipment", r: "3" },
  eq_grippers:           { I: Grip,         s: "equipment", r: "4" },
  eq_jumpropes:          { I: MoveUpRight,  s: "equipment", r: "5" },
  eq_waterbottles:       { I: Droplet,      s: "equipment", r: "6" },
  eq_yogamats:           { I: Layers3,      s: "equipment", r: "7" },
  eq_kettlebells_small:  { I: Anvil,        s: "equipment", r: "8" },
  eq_shakers:            { I: Milk,         s: "equipment", r: "9" },

  // HONOR TILES (matching bottom row of reference: 北 white)
  honor_north: { I: Mountain, s: "strength", r: "δ" },
  honor_white: { I: Snowflake, s: "equipment", r: "白" },
};

const FALLBACK = { I: Dumbbell, s: "strength", r: "1" };

// Track tiles whose PNG asset failed to load so we fall back to Lucide cleanly
// without retrying every render.
const _imageBroken = new Set();

export function TileFace({ type, highlight = false, dim = false, invert = false, size = 32 }) {
  const meta = TILE_META[type] || FALLBACK;
  const Icon = meta.I;
  const numeral = NUMERAL_BY_RANK[meta.r] || meta.r;
  const inkColor = invert ? "#ffffff" : dim ? "#999999" : SUIT_COLOR[meta.s] || "#2F5E3A";
  const figureColor = invert
    ? "#ffffff"
    : dim
    ? "#9AA09A"
    : meta.s === "cardio"
    ? "#7A1F1F"
    : meta.s === "strength"
    ? "#15406F"
    : meta.s === "nutrition"
    ? "#7A5018"
    : meta.s === "reward"
    ? "#7A1F1F"
    : meta.s === "equipment"
    ? "#1E4528"
    : "#1E4528";

  const [imgFailed, setImgFailed] = React.useState(_imageBroken.has(type));
  const showImage = !imgFailed;

  return (
    <div
      data-testid={`tileface-${type}`}
      className="relative flex items-center justify-center h-full w-full select-none"
      style={{ padding: "2px" }}
    >
      {/* Small numeral badge in top-left corner — keeps the classic mahjong
          rank/suit identity glanceable without dominating the tile face. */}
      <span
        className="absolute leading-none font-extrabold pointer-events-none"
        style={{
          top: 2,
          left: 4,
          fontSize: "12px",
          color: inkColor,
          opacity: dim ? 0.55 : 1,
          fontFamily: "'Noto Serif TC', 'DM Serif Display', serif",
          textShadow: invert ? "none" : "0 1px 0 rgba(255,255,255,0.85)",
          letterSpacing: "-0.02em",
          lineHeight: 1,
          zIndex: 2,
        }}
      >
        {numeral}
      </span>

      {/* Figure fills ~95% of the tile face — much easier to read on a
          phone screen than the previous half-and-half split. */}
      <div className="flex items-center justify-center w-full h-full">
        {showImage ? (
          <img
            src={`/tiles/${type}.png`}
            alt=""
            draggable={false}
            onError={() => {
              _imageBroken.add(type);
              setImgFailed(true);
            }}
            style={{
              width: "96%",
              height: "96%",
              objectFit: "contain",
              opacity: dim ? 0.5 : 1,
              filter: invert
                ? "drop-shadow(0 2px 2px rgba(0,0,0,0.5)) brightness(1.6) saturate(0.4)"
                : "drop-shadow(0 1px 0 rgba(255,255,255,0.7))",
              mixBlendMode: invert ? "screen" : "multiply",
            }}
          />
        ) : (
          <Icon
            size={Math.max(size, 36)}
            strokeWidth={2.6}
            style={{
              color: figureColor,
              opacity: dim ? 0.55 : 1,
              filter: invert
                ? "drop-shadow(0 2px 2px rgba(0,0,0,0.5))"
                : "drop-shadow(0 1px 0 rgba(255,255,255,0.7))",
            }}
          />
        )}
      </div>

      {highlight && (
        <span className="absolute inset-0 rounded-[9px] ring-2 ring-[#E8A365] pointer-events-none" />
      )}
    </div>
  );
}
